import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SpaceCard from './SpaceCard';
import SpaceForm from './SpaceForm';

/**
 * Generate a UUID v4
 * @returns {string} A UUID v4 string
 */
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Component to manage venue spaces - add, edit, delete
 */
const SpaceManager = ({
  spaces = [],
  onChange,
  maxSpaces = 10,
  disabled = false,
  error = null
}) => {
  const [openForm, setOpenForm] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);

  const handleOpenForm = () => {
    setEditingSpace(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingSpace(null);
  };

  const handleEditSpace = (space) => {
    setEditingSpace(space);
    setOpenForm(true);
  };

  const handleDeleteSpace = (spaceId) => {
    const updatedSpaces = spaces.filter(space => 
      space.id !== spaceId
    );
    onChange(updatedSpaces);
  };

  const handleSubmitSpace = (spaceData) => {
    let updatedSpaces;
    
    if (editingSpace) {
      updatedSpaces = spaces.map(space => 
        space.id === spaceData.id ? spaceData : space
      );
    } else {
      // Add new space with a GUID
      const newSpace = {
        ...spaceData,
        id: generateGuid()
      };
      updatedSpaces = [...spaces, newSpace];
    }
    
    onChange(updatedSpaces);
    handleCloseForm();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Venue Spaces</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenForm}
          disabled={disabled || spaces.length >= maxSpaces}
        >
          Add Space
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {spaces.length === 0 ? (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            bgcolor: 'background.default' 
          }}
        >
          <Typography color="text.secondary">
            No spaces added yet. Click "Add Space" to create a venue space.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {spaces.map(space => (
            <Grid item xs={12} md={6} key={space.id}>
              <SpaceCard
                space={space}
                onEdit={() => handleEditSpace(space)}
                onDelete={() => handleDeleteSpace(space.id)}
                disabled={disabled}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <SpaceForm
        open={openForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmitSpace}
        editSpace={editingSpace}
        isSubmitting={false}
      />

      {spaces.length > 0 && spaces.length < maxSpaces && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenForm}
          disabled={disabled}
          sx={{ mt: 2 }}
        >
          Add Another Space
        </Button>
      )}
      
      {spaces.length >= maxSpaces && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Maximum limit of {maxSpaces} spaces reached.
        </Alert>
      )}
    </Box>
  );
};

export default SpaceManager; 